import { Component, ElementRef, Inject, AfterViewInit, HostListener, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ImageCropperData {
  imageDataUrl: string;
  fileName: string;
  mimeType: string;
}

type ResizeCorner = 'tl' | 'tr' | 'bl' | 'br';
type DragType = 'move' | ResizeCorner;

@Component({
  selector: 'cpf-image-cropper-modal',
  templateUrl: './image-cropper-modal.component.html',
  styleUrls: ['./image-cropper-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ImageCropperModalComponent implements AfterViewInit {
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  isProcessing = false;

  private img = new Image();
  private ctx!: CanvasRenderingContext2D;
  private cropBox = { x: 0, y: 0, size: 100 };
  private imgScale = 1;

  private dragType: DragType | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartCrop = { x: 0, y: 0, size: 0 };

  private readonly HANDLE_SIZE = 10;
  private readonly HANDLE_HIT = 18;
  private readonly OUTPUT_SIZE = 800;

  constructor(
    public dialogRef: MatDialogRef<ImageCropperModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImageCropperData,
  ) {}

  ngAfterViewInit() {
    this.img.onload = () => {
      this.setupCanvas();
      this.draw();
    };
    this.img.src = this.data.imageDataUrl;
  }

  @HostListener('window:resize')
  onResize() {
    if (!this.ctx || this.dragType) return;

    // Preserve the current crop in source-image coordinates across the rescale.
    const src = {
      x: this.cropBox.x / this.imgScale,
      y: this.cropBox.y / this.imgScale,
      size: this.cropBox.size / this.imgScale,
    };
    this.setupCanvas();
    this.cropBox = {
      x: src.x * this.imgScale,
      y: src.y * this.imgScale,
      size: src.size * this.imgScale,
    };
    this.draw();
  }

  private setupCanvas() {
    const canvas = this.canvasRef.nativeElement;
    const maxDisplay = Math.min(480, window.innerWidth * 0.82);
    const scale = Math.min(maxDisplay / this.img.naturalWidth, maxDisplay / this.img.naturalHeight);
    const drawW = Math.round(this.img.naturalWidth * scale);
    const drawH = Math.round(this.img.naturalHeight * scale);

    canvas.width = drawW;
    canvas.height = drawH;
    this.imgScale = scale;
    this.ctx = canvas.getContext('2d')!;

    const size = Math.min(drawW, drawH);
    this.cropBox = {
      x: (drawW - size) / 2,
      y: (drawH - size) / 2,
      size,
    };
  }

  private draw() {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    const { x, y, size } = this.cropBox;

    // Draw full image
    ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);

    // Darken area outside crop box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Redraw image inside crop box at full brightness
    const srcX = x / this.imgScale;
    const srcY = y / this.imgScale;
    const srcS = size / this.imgScale;
    ctx.drawImage(this.img, srcX, srcY, srcS, srcS, x, y, size, size);

    // Crop border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, size, size);

    // Rule-of-thirds grid lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      const gx = x + (size / 3) * i;
      const gy = y + (size / 3) * i;
      ctx.beginPath();
      ctx.moveTo(gx, y);
      ctx.lineTo(gx, y + size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, gy);
      ctx.lineTo(x + size, gy);
      ctx.stroke();
    }

    // Corner handles
    const h = this.HANDLE_SIZE;
    for (const [hx, hy] of [[x, y], [x + size, y], [x, y + size], [x + size, y + size]]) {
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = 1;
      ctx.fillRect(hx - h / 2, hy - h / 2, h, h);
      ctx.strokeRect(hx - h / 2, hy - h / 2, h, h);
    }
  }

  onPointerDown(event: PointerEvent) {
    const canvas = this.canvasRef.nativeElement;
    const pos = this.getCanvasPos(event);
    const { x, y, size } = this.cropBox;
    const h = this.HANDLE_HIT;

    const inHandle = (hx: number, hy: number) =>
      Math.abs(pos.x - hx) <= h / 2 && Math.abs(pos.y - hy) <= h / 2;

    if (inHandle(x, y)) {
      this.dragType = 'tl';
    } else if (inHandle(x + size, y)) {
      this.dragType = 'tr';
    } else if (inHandle(x, y + size)) {
      this.dragType = 'bl';
    } else if (inHandle(x + size, y + size)) {
      this.dragType = 'br';
    } else if (pos.x >= x && pos.x <= x + size && pos.y >= y && pos.y <= y + size) {
      this.dragType = 'move';
    } else {
      return;
    }

    this.dragStartX = pos.x;
    this.dragStartY = pos.y;
    this.dragStartCrop = { ...this.cropBox };
    canvas.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    const canvas = this.canvasRef.nativeElement;
    const pos = this.getCanvasPos(event);

    if (!this.dragType) {
      this.updateCursor(pos, canvas);
      return;
    }

    const start = this.dragStartCrop;
    const W = canvas.width;
    const H = canvas.height;

    if (this.dragType === 'move') {
      const dx = pos.x - this.dragStartX;
      const dy = pos.y - this.dragStartY;
      this.cropBox = {
        x: Math.max(0, Math.min(start.x + dx, W - start.size)),
        y: Math.max(0, Math.min(start.y + dy, H - start.size)),
        size: start.size,
      };
    } else {
      const corner = this.dragType;
      const fixedX = corner === 'tl' || corner === 'bl' ? start.x + start.size : start.x;
      const fixedY = corner === 'tl' || corner === 'tr' ? start.y + start.size : start.y;

      const maxSize =
        corner === 'tl' ? Math.min(fixedX, fixedY) :
        corner === 'tr' ? Math.min(W - fixedX, fixedY) :
        corner === 'bl' ? Math.min(fixedX, H - fixedY) :
                          Math.min(W - fixedX, H - fixedY);

      const rawSize = Math.max(Math.abs(pos.x - fixedX), Math.abs(pos.y - fixedY));
      const newSize = Math.max(30, Math.min(rawSize, maxSize));

      this.cropBox = {
        x: corner === 'tl' || corner === 'bl' ? fixedX - newSize : fixedX,
        y: corner === 'tl' || corner === 'tr' ? fixedY - newSize : fixedY,
        size: newSize,
      };
    }

    this.draw();
  }

  onPointerUp(_event: PointerEvent) {
    this.dragType = null;
  }

  async confirmCrop() {
    this.isProcessing = true;

    try {
      const srcX = Math.max(0, this.cropBox.x / this.imgScale);
      const srcY = Math.max(0, this.cropBox.y / this.imgScale);
      const srcSize = Math.min(
        this.cropBox.size / this.imgScale,
        Math.min(this.img.naturalWidth - srcX, this.img.naturalHeight - srcY),
      );

      // Never upscale beyond the cropped source resolution.
      const outSize = Math.min(Math.round(srcSize), this.OUTPUT_SIZE);

      const offscreen = document.createElement('canvas');
      offscreen.width = outSize;
      offscreen.height = outSize;
      const octx = offscreen.getContext('2d')!;

      octx.drawImage(this.img, srcX, srcY, srcSize, srcSize, 0, 0, outSize, outSize);

      const baseName = this.data.fileName.replace(/\.[^.]+$/, '') + '.jpg';
      const file = await this.compressToFile(offscreen, baseName);
      this.dialogRef.close(file);
    } catch (error) {
      console.error('Image crop failed', error);
      this.dialogRef.close(null);
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }

  private updateCursor(pos: { x: number; y: number }, canvas: HTMLCanvasElement) {
    const { x, y, size } = this.cropBox;
    const h = this.HANDLE_HIT;

    const corners: { hx: number; hy: number; cursor: string }[] = [
      { hx: x, hy: y, cursor: 'nwse-resize' },
      { hx: x + size, hy: y, cursor: 'nesw-resize' },
      { hx: x, hy: y + size, cursor: 'nesw-resize' },
      { hx: x + size, hy: y + size, cursor: 'nwse-resize' },
    ];

    for (const c of corners) {
      if (Math.abs(pos.x - c.hx) <= h / 2 && Math.abs(pos.y - c.hy) <= h / 2) {
        canvas.style.cursor = c.cursor;
        return;
      }
    }

    canvas.style.cursor =
      pos.x >= x && pos.x <= x + size && pos.y >= y && pos.y <= y + size
        ? 'move'
        : 'default';
  }

  private getCanvasPos(event: PointerEvent) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  private async compressToFile(canvas: HTMLCanvasElement, fileName: string): Promise<File> {
    const blob = await new Promise<Blob>((res, rej) =>
      canvas.toBlob(b => (b ? res(b) : rej(new Error('null blob'))), 'image/jpeg', 0.9));

    return new File([blob], fileName, { type: 'image/jpeg' });
  }
}
